// packages/shared/src/services/wishlist.service.ts
import {
  doc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  getDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { COLLECTIONS } from '@bookstore/shared';
import { getFirebaseFirestore } from '@bookstore/shared/browser';

export class WishlistService {
  private get db() {
    return getFirebaseFirestore();
  }

  /**
   * Add book to user's wishlist
   */
  async addToWishlist(userId: string, productId: string): Promise<void> {
    try {
      const userRef = doc(this.db, COLLECTIONS.USERS, userId);
      await updateDoc(userRef, {
        wishlist: arrayUnion(productId),
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      throw new Error('Failed to add to wishlist');
    }
  }

  /**
   * Remove book from user's wishlist
   */
  async removeFromWishlist(userId: string, productId: string): Promise<void> {
    try {
      const userRef = doc(this.db, COLLECTIONS.USERS, userId);
      await updateDoc(userRef, {
        wishlist: arrayRemove(productId),
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error removing from wishlist:', error);
      throw new Error('Failed to remove from wishlist');
    }
  }

  /**
   * Get user's wishlist book IDs
   */
  async getWishlist(userId: string): Promise<string[]> {
    try {
      const userRef = doc(this.db, COLLECTIONS.USERS, userId);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        return [];
      }

      return userDoc.data()?.wishlist || [];
    } catch (error) {
      console.error('Error getting wishlist:', error);
      throw new Error('Failed to get wishlist');
    }
  }

  /**
   * Check if book is in wishlist
   */
  async isInWishlist(userId: string, productId: string): Promise<boolean> {
    try {
      const wishlist = await this.getWishlist(userId);
      return wishlist.includes(productId);
    } catch (error) {
      console.error('Error checking wishlist:', error);
      return false;
    }
  }

  /**
   * Clear entire wishlist
   */
  async clearWishlist(userId: string): Promise<void> {
    try {
      const userRef = doc(this.db, COLLECTIONS.USERS, userId);
      await updateDoc(userRef, {
        wishlist: [],
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error clearing wishlist:', error);
      throw new Error('Failed to clear wishlist');
    }
  }
}

export const wishlistService = new WishlistService();